import { useEffect, useMemo, useRef, useState } from 'react';
import { Minus, Plus } from 'lucide-react';
import { useLoaderData } from 'react-router';
import { useTracker } from '@archipro-website/tracker';

import Delivery from '@modules/product/component/variant-purchase/Delivery';
import ClickAndCollect from '@modules/product/component/variant-purchase/ClickAndCollect';
import ShippingPopup from '@modules/ecommerce/component/shipping-popup/ShippingPopup';

import { cn } from '@rocco/utils/cn';
import { useVariantSelectionState } from '@modules/product/context/VariantSelectionContext';
import { useVariantOptionState } from '@modules/product/context/VariantOptionContext';
import { useVariantShipping } from '~/modules/product/hook/use-variant-shipping';
import { useVariantStock } from '@modules/product/hook/use-variant-stock';
import { useAddToCartWithTracking } from '@modules/product/hook/use-add-to-cart-with-tracking';
import { useAddToCart } from '@modules/ecommerce/hook/use-add-to-cart';
import { useSampleStock } from '@modules/product/hook/use-sample-stock';
import type { loader } from '~/routes/_app.product.$urlSegment';
import { detailCta } from '~/ui-rocco/pages/detail-shared';
import { fetcherIsDone } from '~/utils/fetcherHelper';
import { toast } from 'sonner';
import type { CartItem } from '~/modules/ecommerce/type/cart-item';

/**
 * Shared building blocks for the revamp purchase flow, used by both the inline
 * mobile flow and the desktop slide-in drawer so the two surfaces stay in sync.
 */

// The mobile frame specs 50px pills with ~14px labels — smaller than the
// desktop design-system pill (52px/16px in detail-cta), on purpose.
export const PILL = (variant: 'filled-light' | 'outlined-light') =>
    detailCta(variant, 'h-[3.125rem] w-full text-sm');

/**
 * What the enquiry CTA says it is about.
 *
 * The reader has just picked a variant, so the button names that rather than
 * the product. A product with one variant has nothing to have picked, and
 * "this variant" would be asking about a choice it never offered.
 */
export const enquiryCtaLabel = (variantCount: number) =>
    variantCount > 1
        ? 'Enquire about this variant'
        : 'Enquire about this product';

/**
 * The legacy `sizesInUse` filter — only the sizes a variant actually uses.
 * Shared by the CTA section, the inline mobile flow and the desktop drawer so
 * the rule lives in one place.
 */
export const useSizesInUse = () => {
    const { product } = useLoaderData<typeof loader>();
    return useMemo(
        () =>
            product.Sizes.filter(
                (s) =>
                    product.Variants.some((v) => v.Size?.ID === s.ID) ||
                    (product.Variants.length === 0 &&
                        product.MasterVariant?.Size?.ID === s.ID),
            ),
        [product],
    );
};

/**
 * Minus / value / plus quantity stepper. Must render inside the shipping
 * providers (reads stock + shipping state).
 */
export const QuantityStepper = () => {
    const { quantity, setQuantity, selectedVariant } =
        useVariantSelectionState();
    const { hasClickAndCollect, hasShippingType } = useVariantShipping();
    const { canAddToCart, quantityLimit } = useVariantStock({
        hasClickAndCollect,
        hasShippingType,
    });

    return (
        <div className="flex items-center gap-1 rounded-[var(--radius-btn-m)] border border-black/20">
            <button
                type="button"
                aria-label="Decrease quantity"
                disabled={!selectedVariant || !canAddToCart || quantity <= 1}
                onClick={() => setQuantity(quantity - 1)}
                className="flex size-11 items-center justify-center disabled:opacity-30"
            >
                <Minus className="size-4" />
            </button>
            <span className="min-w-6 text-center font-sans text-sm">
                {Math.max(quantity, 1)}
            </span>
            <button
                type="button"
                aria-label="Increase quantity"
                disabled={
                    !selectedVariant ||
                    !canAddToCart ||
                    quantity >= quantityLimit
                }
                onClick={() => setQuantity(quantity + 1)}
                className="flex size-11 items-center justify-center disabled:opacity-30"
            >
                <Plus className="size-4" />
            </button>
        </div>
    );
};

/**
 * Inline stock line + quantity stepper, shown when the selected variant is
 * buyable. Must render inside the shipping providers.
 */
export const InlineStockAndQuantity = () => {
    const { hasClickAndCollect, hasShippingType } = useVariantShipping();
    const { stockDescription } = useVariantStock({
        hasClickAndCollect,
        hasShippingType,
    });

    return (
        <div className="flex items-center justify-between">
            <p className="m-0 font-sans text-sm text-[#7f7f7f]">
                {stockDescription}
            </p>
            <QuantityStepper />
        </div>
    );
};

interface InlineAddToCartButtonProps {
    /**
     * Fires after a successful add, with the line it created — the desktop
     * drawer shows it, so its cart pane can confirm the add before the page's
     * own cart summary has caught up.
     */
    onAdded?: (item: CartItem | null) => void;
    /** Open the top-nav cart preview after adding (default). Mobile passes
     *  false and confirms in its own sheet. */
    openCartPopup?: boolean;
    /**
     * Pill style. `filled-light` (default) when cart is the primary action;
     * `outlined-light` when it sits below a primary Enquire CTA (buyable
     * products where the professional also accepts enquiries).
     */
    variant?: 'filled-light' | 'outlined-light';
    /**
     * The button's own classes, for a surface whose CTAs are not the mobile
     * pill — the desktop drawer runs the page's own button instead.
     */
    pill?: string;
    /**
     * The label, where the surface says more than "Add to Cart" — the desktop
     * drawer carries the selection's price in it.
     */
    label?: string;
}

/**
 * The Add to Cart pill. Must render inside the shipping providers.
 */
export const InlineAddToCartButton = ({
    onAdded,
    openCartPopup,
    variant = 'filled-light',
    pill,
    label,
}: InlineAddToCartButtonProps = {}) => {
    const { hasClickAndCollect, hasShippingType } = useVariantShipping();
    const { canAddToCart, stockDescription } = useVariantStock({
        hasClickAndCollect,
        hasShippingType,
    });
    const { addSelectedToCart, isLoading } = useAddToCartWithTracking({
        onItemAdded: onAdded,
        openCartPopup,
    });

    return (
        <button
            type="button"
            disabled={!canAddToCart || isLoading}
            onClick={addSelectedToCart}
            className={cn(pill ?? PILL(variant), 'disabled:opacity-50')}
        >
            {/* A disabled button says why, in the legacy page's words
                ("Out of stock"), rather than offering a cart it cannot fill. */}
            {isLoading
                ? 'Adding…'
                : canAddToCart
                  ? (label ?? 'Add to Cart')
                  : stockDescription}
        </button>
    );
};

interface PurchasePriceBlockProps {
    priceString: string | null;
    isPriceRange: boolean;
    isInServiceableArea: boolean;
    /** True when the current price is discounted — shows the struck-out retail price. */
    isOnSale?: boolean;
    /** Retail (original) price to strike through when isOnSale && !isPriceRange. */
    retailPriceString?: string | null;
    /** Price-range lead-in ("From" / "Up to"), matching legacy VariantPricing. */
    pricePrefix?: string | null;
    /** Column alignment — `end` (drawer, default) or `start` (the mobile
     *  identity block, where the price sits under the left-aligned title). */
    align?: 'start' | 'end';
    /** Smaller type for the pinned sheet/drawer footer, where the price sits
     *  beside the CTAs and tracks the variant selection live. */
    compact?: boolean;
    /** How much one quantity step buys when the price is a rate in another
     *  unit, e.g. "Sold in multiples of 10.76 sq ft". */
    quantityNote?: string | null;
}

/**
 * Price + optional struck-out retail price (A4), price-range asterisk +
 * "*Price indication" annotation (A5), how much one quantity step buys when
 * the rate is in another unit, and the out-of-area notice.
 */
export const PurchasePriceBlock = ({
    priceString,
    isPriceRange,
    isInServiceableArea,
    isOnSale,
    retailPriceString,
    pricePrefix,
    align = 'end',
    compact = false,
    quantityNote,
}: PurchasePriceBlockProps) => {
    if (!priceString && isInServiceableArea) return null;

    const showStrikethrough =
        isOnSale && !isPriceRange && Boolean(retailPriceString);

    return (
        <div
            className={cn(
                'flex flex-col gap-0.5',
                align === 'end' ? 'items-end' : 'items-start',
            )}
        >
            {priceString ? (
                <>
                    {showStrikethrough ? (
                        <del
                            className={cn(
                                'm-0 font-sans font-normal text-[#767676]',
                                compact ? 'text-sm' : 'text-base',
                            )}
                        >
                            {retailPriceString}
                        </del>
                    ) : null}
                    <p
                        className={cn(
                            'm-0 font-sans leading-none text-[#181818]',
                            compact
                                ? 'text-[1.375rem] font-medium tracking-[-0.02em]'
                                : // The entity-title tier, which is the ceiling
                                  // for anything in the identity block: at 34px
                                  // the price was a third larger than the name of
                                  // the thing it prices. Lighter than the title
                                  // at the same size, so the title still leads.
                                  'text-[1.625rem] font-normal tracking-[-0.025em]',
                        )}
                    >
                        {pricePrefix ? (
                            <span className="mr-1.5 align-middle text-[0.45em] font-normal tracking-normal">
                                {pricePrefix}
                            </span>
                        ) : null}
                        {priceString}
                        {isPriceRange ? (
                            <span className="align-super text-[0.45em] text-[#767676]">
                                *
                            </span>
                        ) : null}
                    </p>
                    {isPriceRange ? (
                        <p
                            className={cn(
                                'm-0 font-sans text-[#767676]',
                                compact ? 'text-xs' : 'text-sm',
                            )}
                        >
                            *Price indication
                        </p>
                    ) : null}
                    {quantityNote ? (
                        <p
                            className={cn(
                                'm-0 font-sans text-[#767676]',
                                compact ? 'text-xs' : 'text-sm',
                            )}
                        >
                            {quantityNote}
                        </p>
                    ) : null}
                </>
            ) : null}
            {!isInServiceableArea ? (
                <p className="m-0 font-sans text-sm text-[#6b6b6b]">
                    Not available in your location.
                </p>
            ) : null}
        </div>
    );
};

/**
 * Pill button that adds the selected variant's sample to the cart. Sits in the
 * pinned drawer / mobile CTA footer so it stays visible alongside the main CTA
 * — mirrors the "Order Sample ({priceText})" the legacy SamplePurchase renders,
 * but in the revamp pill style. Must render inside VariantShippingMethodsProvider
 * + ItemShippingProvider.
 */
export const InlineSamplePurchaseButton = ({
    onAdded,
    pill,
}: {
    /** The button's own classes — see {@link InlineAddToCartButtonProps.pill}. */
    pill?: string;
    /** Called once per successful sample add with the line it added, so the
     *  drawer can show it in its own cart panel, as Add to Cart does. */
    onAdded?: (item: CartItem | null) => void;
} = {}) => {
    const { selectedVariant } = useVariantSelectionState();
    const { canPurchase, priceText } = useSampleStock(selectedVariant);
    const { shipping } = useVariantShipping(true);
    // The drawer confirms the add in its own cart panel, so the header's cart
    // menu stays shut.
    const [addToCart, fetcher] = useAddToCart(true, {
        openCartMenu: false,
    });
    const [regionPickerOpen, setRegionPickerOpen] = useState(false);
    const { shoppingCartSummary } = useLoaderData<typeof loader>();
    const tracker = useTracker();

    // The legacy sample events: the press, then the add itself once a
    // shipping region is settled.
    const addSampleToCart = (variant: NonNullable<typeof selectedVariant>) => {
        void tracker.log('AddToCartSample', {
            url: new URL(window.location.href),
            targetTracker: 'archiproTracker',
            data: {
                // format: VariantID,CartID
                ExtraData: `${variant.ID},${shoppingCartSummary?.ID || 0}`,
            },
        });
        addToCart(variant, 1);
    };

    const handleClick = () => {
        if (!selectedVariant) return;
        void tracker.log('OrderSampleSelect', {
            url: new URL(window.location.href),
            targetTracker: 'archiproTracker',
            data: { ExtraData: selectedVariant.ID },
        });
        // No sample shipping resolved yet (e.g. no region chosen) —
        // useAddToCart would silently no-op, so open the region picker first
        // and add on confirm (the legacy mobile SamplePurchase flow).
        if (!shipping) {
            setRegionPickerOpen(true);
            return;
        }
        addSampleToCart(selectedVariant);
    };

    // Fire onAdded / the error toast exactly once per completed add (the
    // fetcher keeps its data after settling, so guard against re-runs — same
    // pattern as useAddToCartWithTracking).
    const processedData = useRef<unknown>(null);
    const onAddedRef = useRef(onAdded);
    onAddedRef.current = onAdded;
    useEffect(() => {
        if (!fetcherIsDone(fetcher) || !fetcher.data) return;
        if (processedData.current === fetcher.data) return;
        processedData.current = fetcher.data;
        if ('error' in fetcher.data) {
            toast.error(fetcher.data.message || 'Unable to add sample to cart');
        } else {
            onAddedRef.current?.(
                fetcher.data.addToShoppingCart.LastAddedItems[0] ?? null,
            );
        }
    }, [fetcher]);

    if (!canPurchase) return null;

    return (
        <>
            <button
                type="button"
                disabled={fetcher.state !== 'idle'}
                onClick={handleClick}
                className={cn(
                    pill ?? PILL('outlined-light'),
                    'disabled:opacity-50',
                )}
            >
                {fetcher.state !== 'idle'
                    ? 'Adding…'
                    : `Order Sample (${priceText})`}
            </button>
            <ShippingPopup
                open={regionPickerOpen}
                onCancel={() => setRegionPickerOpen(false)}
                onConfirm={() => {
                    setRegionPickerOpen(false);
                    if (selectedVariant) {
                        addSampleToCart(selectedVariant);
                    }
                }}
                isSample
                confirmButtonText="Confirm"
            />
        </>
    );
};

/**
 * Sample delivery/collection info for sample-only products — the shipping rows
 * the legacy SamplePurchase showed (Delivery + ClickAndCollect with the region
 * picker popup), without its embedded order button (the drawer footer's
 * `InlineSamplePurchaseButton` owns the action). Must render inside
 * VariantShippingMethodsProvider + ItemShippingProvider.
 */
export const SampleShippingInfo = () => {
    const { selectedVariant } = useVariantSelectionState();
    const { canPurchase } = useSampleStock(selectedVariant);
    const [regionPickerOpen, setRegionPickerOpen] = useState(false);

    if (!canPurchase) return null;

    return (
        <div className="flex flex-col gap-1">
            <Delivery
                isSample
                onRegionClick={() => setRegionPickerOpen(true)}
            />
            <ClickAndCollect
                isSample
                onLocationClick={() => setRegionPickerOpen(true)}
            />
            <ShippingPopup
                open={regionPickerOpen}
                onCancel={() => setRegionPickerOpen(false)}
                onConfirm={() => setRegionPickerOpen(false)}
                isSample
                confirmButtonText="Confirm"
            />
        </div>
    );
};

interface VariantEnquiryContext {
    /** Product title with the selected variant folded in, for the message + header. */
    messageSubject: string;
    /** The selected variant's thumbnail for the modal header (undefined → product default). */
    itemImage?: string;
    /** The selected variant's detail-page image for the gate's brand pane. */
    brandImage?: string;
}

/**
 * Soft-attaches the selected variant to a quote enquiry. The enquiry payload has
 * no structured variant field, so we fold the selected options/size into the
 * message subject and swap the header image to the variant's. Returns the
 * `messageSubject` + images to pass to `openEnquiryModal`.
 */
export const useVariantEnquiryContext = (): VariantEnquiryContext => {
    const { product } = useLoaderData<typeof loader>();
    const { selectedVariant } = useVariantSelectionState();
    const { attributeMap, decision } = useVariantOptionState();

    const optionLabels = [...attributeMap.values()]
        .map(
            (attribute) =>
                attribute.options.find(
                    (option) => option.ID === decision.options[attribute.ID],
                )?.Label,
        )
        .filter((label): label is string => Boolean(label));
    const sizeLabel = decision.size
        ? product.Sizes.find((size) => size.ID === decision.size)?.FullTitle
        : undefined;
    const parts = [...optionLabels, sizeLabel].filter(Boolean);
    const messageSubject = parts.length
        ? `${product.Title} (${parts.join(', ')})`
        : product.Title;

    const variantImage =
        (selectedVariant?.Image &&
            product.LibraryImages.find(
                (image) => image.ID === selectedVariant.Image?.ID,
            )) ||
        product.LibraryImages[0];
    const itemImage = variantImage?.LibraryImageFile?.PhotoTile ?? undefined;
    // The gate's pane prefers brandImage, so the variant has to replace both or
    // the base product stays behind the gate.
    const brandImage =
        variantImage?.LibraryImageFile?.DetailPageSquare?.Desktop ?? undefined;

    return { messageSubject, itemImage, brandImage };
};
